import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Instagram, Facebook, MapPin, Phone, ArrowRight } from 'lucide-react';
import { navLinks, contact } from '../mock/mock';
import logoCream from '../assets/logo-cream.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: 'footer' });
      setSent(true);
      setEmail('');
      setTimeout(() => setSent(false), 3500);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('newsletter subscribe failed', err);
      setSent(true); // still acknowledge; avoid dead-end UX
      setEmail('');
      setTimeout(() => setSent(false), 3500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative bg-[#2C3A28] text-[#EDEAE0] overflow-hidden">
      <div className="grain" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr_0.8fr_0.85fr_0.8fr_1fr]">
          {/* brand */}
          <div className="order-1">
            <div className="flex items-center gap-2">
              <img src={logoCream} alt="Al Naturale" className="h-11 w-auto" />
            </div>
            <p className="mt-4 max-w-sm text-white leading-relaxed">
              Homemade nut milks, fresh flavours, and wholesome goodness in the heart of
              St Kilda.
            </p>
          </div>

          {/* main menu */}
          <div className="order-3">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white">Menu</h4>
            <ul className="mt-5 space-y-3">
              {navLinks
                .filter((l) => ['/', '/about', '/products', '/menu', '/cafe'].includes(l.to))
                .map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="inline-flex items-center gap-2 text-white hover:text-[#D2B489] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* discover */}
          <div className="order-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white">Discover</h4>
            <ul className="mt-5 space-y-3">
              {navLinks
                .filter((l) => ['/gift-cards', '/where-to-buy', '/catering', '/classes'].includes(l.to))
                .map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="inline-flex items-center gap-2 text-white hover:text-[#D2B489] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* get social */}
          <div className="order-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white">Get Social</h4>
            <div className="mt-5 space-y-5">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#EDEAE0] font-semibold">Milk</p>
                <ul className="mt-2.5 space-y-2.5 text-white">
                  <li>
                    <a
                      href="https://www.instagram.com/alnaturalemilk/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-instagram-milk"
                      className="inline-flex items-center gap-3 hover:text-[#D2B489] transition-colors"
                    >
                      <Instagram size={18} strokeWidth={2.75} className="text-white" />
                      @alnaturalemilk
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.facebook.com/p/Alnaturalemilk-61569125177233/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-facebook-milk"
                      className="inline-flex items-center gap-3 hover:text-[#D2B489] transition-colors"
                    >
                      <Facebook size={18} strokeWidth={2.75} className="text-white" />
                      fb.alnaturalemilk
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#EDEAE0] font-semibold">Cafe</p>
                <ul className="mt-2.5 space-y-2.5 text-white">
                  <li>
                    <a
                      href="https://www.instagram.com/alnaturalecafe/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-instagram-cafe"
                      className="inline-flex items-center gap-3 hover:text-[#D2B489] transition-colors"
                    >
                      <Instagram size={18} strokeWidth={2.75} className="text-white" />
                      @alnaturalecafe
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.facebook.com/p/Alnaturalecafe-61559516887664/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-facebook-cafe"
                      className="inline-flex items-center gap-3 hover:text-[#D2B489] transition-colors"
                    >
                      <Facebook size={18} strokeWidth={2.75} className="text-white" />
                      fb.alnaturalecafe
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* get in touch */}
          <div className="order-6">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white">Get in touch</h4>
            <ul className="mt-5 space-y-4 text-white">
              <li className="flex items-start gap-3">
                <MapPin size={18} strokeWidth={2.75} className="mt-0.5 text-white" />
                <span>{contact.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} strokeWidth={2.75} className="text-white" />
                <a href={`tel:${contact.phone}`} className="hover:text-[#D2B489]">
                  {contact.phone}
                </a>
              </li>
            </ul>
          </div>

          {/* resources */}
          <div className="order-5">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white">Resources</h4>
            <ul className="mt-5 space-y-3">
              {navLinks
                .filter((l) => l.to === '/faq' || l.to === '/blog' || l.to === '/careers')
                .map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="inline-flex items-center gap-2 text-white hover:text-[#D2B489] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#EDEAE0]/12">
          <div
            className="flex flex-col items-center gap-4 text-center"
            data-testid="acknowledgement-of-country"
          >
            <svg
              viewBox="0 0 60 40"
              className="w-12 h-8 shrink-0 rounded-sm shadow-[0_0_0_1px_rgba(243,231,211,0.15)]"
              role="img"
              aria-label="Aboriginal flag"
            >
              <rect width="60" height="20" fill="#000000" />
              <rect y="20" width="60" height="20" fill="#CC0000" />
              <circle cx="30" cy="20" r="8" fill="#FFCC00" />
            </svg>
            <p className="text-[13px] leading-relaxed text-white max-w-3xl">
              We acknowledge the Traditional Custodians of the land on which we live and
              work. We pay our respects to Elders past and present, and honour their
              enduring connection to Country, culture and community.
            </p>
          </div>
        </div>

        <div className="mt-10 pt-7 border-t border-[#EDEAE0]/12 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white">
          <p>© {new Date().getFullYear()} Al Naturale. All rights reserved.</p>
          <p className="flex flex-wrap items-center justify-center sm:justify-start gap-x-1.5 gap-y-1">
            <span>Crafted fresh in St Kilda by</span>
            <a
              href="https://lcreativestudio.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono uppercase tracking-[0.25em] text-[#FF5500] border-b-2 border-[#FF5500] pb-0.5 hover:text-[#FF6A1F] hover:border-[#FF6A1F] transition-colors"
            >
              LCreative Studio
            </a>
            <span>Melbourne · No additives, ever.</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
